type QueryValue = string | string[] | number | number[] | boolean | null | undefined | QueryObject
type QueryObject = { [key: string]: QueryValue }

export type Request = {
  path?: string
  query?: QueryObject
  body?: Record<string, unknown> | unknown[]
  headers?: Partial<RequestInit['headers']>
}

export class NotFoundApiError extends Error {
  constructor() {
    super('NotFound')
  }
}
export class BadRequestApiError extends Error {
  constructor() {
    super('BadRequest')
  }
}
export class UnauthorizedApiError extends Error {
  constructor() {
    super('Unauthorized')
  }
}
export class InternalServerApiError extends Error {
  constructor() {
    super('InternalServerError')
  }
}
export class UnknownApiError extends Error {
  constructor() {
    super('Unknown')
  }
}

export class BaseApi {
  url: string

  constructor(host: string, pathname: string) {
    this.url = [host, pathname].join('/')
  }
  private parseQuery(data?: Request['query']) {
    if (!data) return ''

    const params: string[] = []

    const stringifyValue = (key: string, value: QueryValue, parentKey?: string): void => {
      const fullKey = parentKey ? `${parentKey}[${key}]` : key

      if (value === null || value === undefined) {
        return
      }

      if (Array.isArray(value)) {
        value.forEach((item, index) => {
          stringifyValue(String(index), item, fullKey)
        })
      } else if (typeof value === 'object' && value !== null) {
        Object.entries(value).forEach(([nestedKey, nestedValue]) => {
          stringifyValue(nestedKey, nestedValue, fullKey)
        })
      } else {
        params.push(`${encodeURIComponent(fullKey)}=${encodeURIComponent(String(value))}`)
      }
    }

    Object.entries(data).forEach(([key, value]) => {
      stringifyValue(key, value)
    })

    return params.length > 0 ? `?${params.join('&')}` : ''
  }

  private parseError(response: Response) {
    if (response.status === 400 || response.status === 422) {
      return new BadRequestApiError()
    }
    if (response.status === 401) {
      return new UnauthorizedApiError()
    }
    if (response.status === 404) {
      return new NotFoundApiError()
    }
    if (response.status === 500) {
      return new InternalServerApiError()
    }
    return new UnknownApiError()
  }

  private sanitizeUrl(path?: string | number) {
    return [this.url, path].filter(Boolean).join('/')
  }

  protected async fetch<T>(method: RequestInit['method'], request?: Request) {
    try {
      const headers = request?.headers
        ? Object.entries(request.headers).reduce<Record<string, string>>((acc, [key, value]) => {
            if (value !== undefined) {
              acc[key] = value
            }
            return acc
          }, {})
        : undefined

      const response = await fetch(
        this.sanitizeUrl(request?.path) + this.parseQuery(request?.query),
        {
          method,
          body: request?.body ? JSON.stringify(request.body) : undefined,
          headers,
        },
      )

      if (!response.ok) {
        throw this.parseError(response)
      }
      return (await response.json()) as T
    } catch (error: unknown) {
      throw error instanceof Error ? error : new UnknownApiError()
    }
  }

  async get<T>(request?: Request) {
    return this.fetch<T>('GET', request)
  }

  async post<T>(request?: Request) {
    return this.fetch<T>('POST', request)
  }

  async put<T>(request?: Request) {
    return this.fetch<T>('PUT', request)
  }

  async delete<T>(request?: Request) {
    return this.fetch<T>('DELETE', request)
  }
}
